import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { STEPS } from '@/lib/index';

interface ProgressBarProps { currentStep: number; }

export function ProgressBar({ currentStep }: ProgressBarProps) {
  return (
    <div className="w-full mb-8">
      <div className="flex items-center justify-between mb-3">
        {STEPS.map((step, i) => (
          <div key={step.id} className="flex items-center flex-1">
            <div className="flex flex-col items-center">
              <motion.div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-all duration-300 ${
                  currentStep > step.id ? 'bg-primary text-primary-foreground' :
                  currentStep === step.id ? 'bg-secondary text-secondary-foreground ring-2 ring-secondary ring-offset-2' :
                  'bg-muted text-muted-foreground'
                }`}
                animate={{ scale: currentStep === step.id ? 1.1 : 1 }}
              >
                {currentStep > step.id ? <Check size={14} /> : step.id}
              </motion.div>
              <span className={`text-[10px] mt-1 font-medium hidden sm:block whitespace-nowrap ${
                currentStep === step.id ? 'text-secondary' : 'text-muted-foreground'
              }`}>{step.label}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div className="flex-1 h-0.5 mx-1 rounded-full overflow-hidden bg-muted">
                <motion.div
                  className="h-full bg-primary rounded-full"
                  initial={{ width: '0%' }}
                  animate={{ width: currentStep > step.id ? '100%' : '0%' }}
                  transition={{ duration: 0.4, ease: 'easeInOut' }}
                />
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="text-center">
        <span className="text-xs text-muted-foreground font-medium">
          Step {currentStep} of {STEPS.length} — {STEPS[currentStep - 1]?.label}
        </span>
      </div>
    </div>
  );
}

interface MultiSelectProps {
  options: string[]; selected: string[];
  onChange: (val: string[]) => void; max?: number; cols?: number;
}
export function MultiSelect({ options, selected, onChange, max, cols = 2 }: MultiSelectProps) {
  const toggle = (opt: string) => {
    if (selected.includes(opt)) { onChange(selected.filter(s => s !== opt)); }
    else if (!max || selected.length < max) { onChange([...selected, opt]); }
  };
  return (
    <div className={`grid gap-2 ${cols === 3 ? 'grid-cols-3' : cols === 1 ? 'grid-cols-1' : 'grid-cols-2'} sm:grid-cols-${cols === 1 ? 1 : cols}`}>
      {options.map(opt => (
        <motion.button key={opt} type="button" onClick={() => toggle(opt)} whileTap={{ scale: 0.97 }}
          className={`px-3 py-2.5 rounded-xl text-sm font-medium text-left transition-all duration-200 border-2 ${
            selected.includes(opt)
              ? 'border-secondary bg-secondary/10 text-secondary font-semibold'
              : 'border-border bg-card text-card-foreground hover:border-primary/50 hover:bg-primary/5'
          }`}>
          {selected.includes(opt) && <Check size={12} className="inline mr-1 mb-0.5" />}
          {opt}
        </motion.button>
      ))}
    </div>
  );
}

interface RadioSelectProps { options: string[]; value: string; onChange: (v: string) => void; cols?: number; }
export function RadioSelect({ options, value, onChange, cols = 2 }: RadioSelectProps) {
  return (
    <div className={`grid gap-2 ${cols === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
      {options.map(opt => (
        <motion.button key={opt} type="button" onClick={() => onChange(opt)} whileTap={{ scale: 0.97 }}
          className={`px-3 py-2.5 rounded-xl text-sm font-medium text-left transition-all duration-200 border-2 ${
            value === opt
              ? 'border-secondary bg-secondary/10 text-secondary font-semibold'
              : 'border-border bg-card text-card-foreground hover:border-primary/50 hover:bg-primary/5'
          }`}>
          {value === opt && <Check size={12} className="inline mr-1 mb-0.5" />}
          {opt}
        </motion.button>
      ))}
    </div>
  );
}

interface FieldProps { label: string; required?: boolean; hint?: string; children: React.ReactNode; }
export function Field({ label, required, hint, children }: FieldProps) {
  return (
    <div className="space-y-2">
      <label className="block text-sm font-semibold text-foreground font-serif">
        {label}{required && <span className="text-secondary ml-1">*</span>}
      </label>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      {children}
    </div>
  );
}

export function SectionCard({ title, emoji, children }: { title: string; emoji?: string; children: React.ReactNode }) {
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: 'easeOut' }}
      className="bg-card rounded-2xl border border-border p-6 space-y-5 shadow-sm">
      <h3 className="font-serif text-lg font-semibold text-foreground flex items-center gap-2">
        {emoji && <span>{emoji}</span>}{title}
      </h3>
      {children}
    </motion.div>
  );
}

export const inputClass = "w-full px-4 py-3 rounded-xl border-2 border-input bg-card text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors duration-200";
export const textareaClass = `${inputClass} resize-none min-h-[90px]`;
